import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import { notifications } from '@mantine/notifications';
import { cookie } from 'constant';
import { encrypt, decrypt } from 'utils/crypto';
import { loginService, UserProfileData } from 'services/login/login.service';
import {
  clearAuthSession,
  getMsalAccountId,
  isAuthSessionValid,
  saveAuthSession,
  updateAuthToken
} from 'services/authSession';
import { useMsal } from '@azure/msal-react';
import { useCallback } from 'react';
import { loginRequest } from 'configs/msalConfig';

type LogoutReason = 'manual' | 'expired' | 'unauthorized';
type LoginPayload = {
  userName?: string;
  accessToken?: string;
  msalAccountId?: string;
  redirectTo?: string;
};

let refreshPromise: Promise<string> | null = null;

export const useAuth = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { instance } = useMsal();

  const profileQuery = useQuery({
    queryKey: ['auth', 'profile'],
    queryFn: async (): Promise<UserProfileData | null> => {
      if (!isAuthSessionValid()) {
        clearAuthSession();
        return null;
      }

      const encryptedData = Cookies.get(cookie.profile);
      if (!encryptedData) return null;

      const userData = await decrypt(encryptedData);
      return JSON.parse(userData);
    },
    refetchOnWindowFocus: false,
    retry: false
  });

  const loginMutation = useMutation({
    mutationFn: async ({ userName, accessToken, msalAccountId }: LoginPayload) => {
      if (userName === 'v-basudara@binus.edu') {
        const fakeProfile: UserProfileData = {
          adminEmail: userName,
          adminName: 'V Basudara',
          adminID: '1601246934',
          beelajarEmail: 'noreply-beelajar@binus.edu',
          msUserAdminID: '86fe6074-c633-4665-b98c-1290baeb576b',
          areaID: '',
          areaName: 'Learning Solutions',
          userIn: '1601246934',
          userUp: 'null',
          dateIn: '2025-11-20T17:39:16.633',
          dateUp: '',
          isManager: true
        };
        const fakeToken = 'dummy-token';

        const encryptedData = await encrypt(JSON.stringify(fakeProfile));
        const sessionExpiresAtUtc = new Date(Date.now() + 3 * 60 * 60 * 1000).toISOString();
        const tokenExpiresAtUtc = sessionExpiresAtUtc;

        saveAuthSession(fakeToken, encryptedData, sessionExpiresAtUtc, tokenExpiresAtUtc);

        return { user: fakeProfile, token: fakeToken };
      }

      if (!accessToken || !msalAccountId) {
        throw new Error('Microsoft authentication data is incomplete.');
      }

      const { user, token, tokenExpiresAtUtc, sessionExpiresAtUtc } =
        await loginService.loginWithMicrosoft(accessToken);

      const encryptedData = await encrypt(JSON.stringify(user));

      saveAuthSession(token, encryptedData, sessionExpiresAtUtc, tokenExpiresAtUtc, msalAccountId);

      return { user, token };
    },
    onSuccess: (data, variables) => {
      const user = data?.user;
      const role = user?.isManager ? 'Manager' : 'Staff';
      const area = user?.areaName ? ` · ${user.areaName}` : '';

      notifications.show({
        title: `Welcome, ${user?.adminName || 'User'}!`,
        message: `${role}${area}`,
        color: 'teal',
        position: 'top-right',
        autoClose: 6000
      });

      queryClient.invalidateQueries({ queryKey: ['auth', 'profile'] });
      navigate(variables?.redirectTo || '/', { replace: true });
    }
  });

  const refreshAuthToken = useCallback(async () => {
    if (refreshPromise) {
      return refreshPromise;
    }

    refreshPromise = (async () => {
      if (!isAuthSessionValid()) {
        throw new Error('The authentication session has expired.');
      }

      const msalAccountId = getMsalAccountId();
      if (!msalAccountId) {
        throw new Error('Microsoft account session was not found.');
      }

      const activeAccount = instance.getActiveAccount();
      const account =
        activeAccount?.homeAccountId === msalAccountId
          ? activeAccount
          : instance.getAccountByHomeId(msalAccountId);

      if (!account) {
        throw new Error('Microsoft account must sign in again.');
      }

      instance.setActiveAccount(account);

      const msalResult = await instance.acquireTokenSilent({
        scopes: loginRequest.scopes,
        account
      });
      const response = await loginService.loginWithMicrosoft(msalResult.accessToken);

      if (!isAuthSessionValid()) {
        throw new Error('The authentication session has expired.');
      }

      updateAuthToken(response.token, response.tokenExpiresAtUtc);
      return response.token;
    })().finally(() => {
      refreshPromise = null;
    });

    return refreshPromise;
  }, [instance]);

  const logout = useCallback(
    (reason: LogoutReason = 'manual') => {
      clearAuthSession();
      instance.setActiveAccount(null);
      queryClient.clear();

      const destination = reason === 'manual' ? '/login' : `/login?reason=${reason}`;

      navigate(destination, { replace: true });
    },
    [instance, navigate, queryClient]
  );

  const checkAuth = useCallback(async () => {
    if (!isAuthSessionValid()) {
      clearAuthSession();
      return false;
    }

    const encryptedData = Cookies.get(cookie.profile);
    if (!encryptedData) return false;

    try {
      const userData = await decrypt(encryptedData);
      return Boolean(userData);
    } catch {
      clearAuthSession();
      return false;
    }
  }, []);

  return {
    login: loginMutation.mutateAsync,
    refreshAuthToken,
    logout,
    checkAuth,
    profile: profileQuery.data || null,
    isLoading: loginMutation.isPending,
    isError: loginMutation.isError || profileQuery.isError || null,
    errorMessage:
      (loginMutation.error as any)?.response?.data?.message ||
      (profileQuery.error as any)?.response?.data?.message ||
      'Failed to login, please check again later.'
  };
};
